import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { AddAccountWebComponent } from '../component/AddAccountWebComponent';
import { AddAccountWebView } from '../view/AddAccountWebView';
import { templateAddAccount } from '../view/template';
import { CreateAccountEvents, NewAccount, LoggedInUserData } from '../types';
import { showToast } from '../../helpers/showToast';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { translateFn } from '../../../utils/translator.helper';
import { AccountDetailsEvents } from '../../common/accountDetails/types';
import { AccountLoginSecurityEvents } from '../../common/accountLoginSecurity/types';
import { isHidden, toggleVisibility } from '../../../utils/htmlElement.utils';
import { SelectedTargets, TargetsViewData } from '../../../targets/type';
import { AccountSource, AccountSourceId } from '../../common/types/AccountSources';
import { LoggedInUserDataProvider } from '../../dataProvider/LoggedInUserDataProvider';
import { UserFormSelectedCompanyDataProvider } from '../../dataProvider/UserFormSelectedCompanyDataProvider';
import { combineLatest, Subscription } from 'rxjs';
import { AccountEvents } from '../../common/types/AccountsEvents';
import { renderSelectTargetsComponent } from '../../helpers/renderSelectTargetsComponent';
import { AccountDataProvider } from '../../dataProvider/AccountDataProvider';

export class AddAccountWebController extends BaseWebComponentController {
  public component: AddAccountWebComponent;
  public view: AddAccountWebView;
  public viewConstructor: typeof AddAccountWebView;
  private userFormSelectedCompanyDataProvider: UserFormSelectedCompanyDataProvider;
  private formSubscription: Subscription;
  private isFormValid: boolean;
  private accountSource: AccountSourceId = AccountSource.MANUAL;

  constructor(component: AddAccountWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: AddAccountWebView,
      viewTemplate: templateAddAccount,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$details,
        event: AccountDetailsEvents.COMPANY_SELECTION_CHANGED,
        callback: this.onSelectCompany,
      },
      {
        element: this.view.dom.$loginSecurity,
        event: AccountLoginSecurityEvents.AUTHENTICATION_METHOD_CHANGED,
        callback: this.onAuthenticationMethodChange,
      },
      {
        element: this.view.dom.$details,
        event: AccountDetailsEvents.CHANGE_ACCOUNT_SOURCE,
        callback: this.onChangeAccountSource,
      },
      {
        element: this.view.dom.$details,
        event: AccountDetailsEvents.RESYNC_AD_IN_PROGRESS,
        callback: this.onADSyncInProgress,
      },
      {
        element: this.view.dom.$details,
        event: AccountDetailsEvents.RESYNC_AD_DONE,
        callback: this.onADSyncDone,
      },
      {
        element: this.view.dom.$roleAndPrivileges,
        event: AccountEvents.ToggleSelectTargets,
        callback: this.toggleSelectTargets,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeCreateAccountForm,
      },
      {
        element: this.view.dom.$createButton,
        event: 'click',
        callback: this.submitCreateAccountForm,
      },
      {
        element: this.view.dom.$wrapper,
        event: CreateAccountEvents.InitCreateAccountForm,
        callback: this.setFormValidation,
      },
    ];
  }

  private async onSelectCompany(event: WebComponentEvent): Promise<void> {
    if (!event.detail.selectedValue) {
      return;
    }

    // Update form fields and the currentCompany data (identityProvider, rights) when user select a company
    if (!this.userFormSelectedCompanyDataProvider) {
      this.userFormSelectedCompanyDataProvider = new UserFormSelectedCompanyDataProvider();
    }
    this.view.currentCompany = await this.userFormSelectedCompanyDataProvider.getUserFormSelectedCompanyData(
      event.detail.selectedValue
    );

    await this.view.changeFormFieldsVisibilityDependingOnSelectedCompany();
  }

  private closeCreateAccountForm(): void {
    this.component.dispatchEvent(
      new CustomEvent(CreateAccountEvents.Cancel, {
        bubbles: true,
      })
    );
  }

  private async submitCreateAccountForm() {
    if (!this.isFormValid) {
      this.setFormValidation();
      return;
    }

    try {
      await this.createAccountRecord();

      showToast(ToastSeverity.SUCCESS, translateFn('Account successfully created.'));
      this.component.dispatchEvent(
        new CustomEvent(CreateAccountEvents.AccountCreated, {
          bubbles: true,
        })
      );
    } catch (error) {
      showToast(ToastSeverity.ERROR, error.err.message);
    }
  }

  private async createAccountRecord(): Promise<any> {
    this.view.dom.$createButton.setLoadingState(true);

    try {
      let record = {
        userName: !this.dependencies.isCloud() ? this.view.dom.$details.getUsernameValue() : '',
        email: this.view.dom.$details.getEmailValue(),
        active: '',
        created: null,
        readOnly: false,
        password: (this.accountSource === AccountSource.MANUAL) && this.dependencies.isCloud() ? '' : this.view.dom.$details.getPasswordValue(),
        profile: {
          fullName: this.view.dom.$details.getFullNameValue(),
          timezone: this.view.dom.$details.getSelectedTimezone(),
          language: this.view.dom.$details.getSelectedLanguage(),
        },
        phoneNumber: {
          countryCode: this.view.dom.$details.getSelectedCountryCode(),
          subscriberNumber: this.view.dom.$details.getPhoneNumberValue(),
        },
        concurrentSessions: {
          enabled: this.view.dom.$loginSecurity.getConcurrentSessions(),
        },
        source: this.accountSource,
        company: {
          id: this.view.currentCompany._id,
        },
        role: {
          roleId: this.view.dom.$roleAndPrivileges.getSelectedRoleId(),
          rights: this.view.dom.$roleAndPrivileges.getSelectedRights(),
        },
      } as NewAccount;

      if (
        ([AccountSource.VIFOR, AccountSource.MANUAL, AccountSource.NEXUS].includes(this.accountSource)) &&
        this.dependencies.isCloud()
      ) {
        record.accountLockdown = {
          enabled: this.view.dom.$loginSecurity.getAccountLockDown(),
        };
        record.passwordLifetime = this.view.dom.$loginSecurity.getPasswordLifetime();
      }

      record.serviceTargets = [] as SelectedTargets;
      if (!isHidden(this.view.dom.$targetsSelection)) {
        record.serviceTargets = this.view.dom.$targetsSelection.getSelectedTargets();
      }

      let records = [record];
      const dataProvider = new AccountDataProvider();
      return await dataProvider.createRecords(records);
    } finally {
      this.view.dom.$createButton.setLoadingState(false);
    }
  }

  private async toggleSelectTargets(event: WebComponentEvent): Promise<void> {
    // remove select targets if this should not be visible
    if (!event.detail.targetsShouldBeVisible) {
      toggleVisibility(this.view.dom.$targetsSelection, false);
      this.setFormValidation();
      return;
    }

    // Toggle select targets depending on selected rights
    if (event.detail.targetsShouldBeVisible) {
      toggleVisibility(this.view.dom.$targetsSelection, true);

      const showOnlyCompanies = this.view.dom.$roleAndPrivileges.hasUserSelectedRightsOnlyForCompanies();
      const viewData = {
        companyId: this.view.currentCompany._id,
        availableServices: this.dependencies.getAvailableServices(),
        showOnlyCompanies: showOnlyCompanies,
        userHasRightsForNetworkTargets: this.view.dom.$roleAndPrivileges.hasUserSelectedRightsForNetworkTargets(),
      } as TargetsViewData;

      if (this.view.preselectedTargets) {
        viewData.preSelectedTargets = this.view.preselectedTargets;
      }

      // start select targets component if doesn't exist
      if (!this.view.dom.$targetsSelection.started) {
        await renderSelectTargetsComponent(this.view.dom.$targetsSelection, viewData, this.dependencies);
        this.setFormValidation();
        return;
      }

      // refresh the component if options has been changed
      if (
        this.view.dom.$targetsSelection.getShowOnlyCompaniesFlag() !== showOnlyCompanies ||
        this.view.dom.$targetsSelection.getCurrentCompanyId() !== this.view.currentCompany._id
      ) {
        await renderSelectTargetsComponent(this.view.dom.$targetsSelection, viewData, this.dependencies);
      }

      this.setFormValidation();
    }
  }

  private onChangeAccountSource(event: WebComponentEvent): void {
    this.accountSource = AccountSource.MANUAL;
    if (event.detail.checked) {
      this.accountSource = AccountSource.ACTIVEDIR;
    }
  }

  private onAuthenticationMethodChange(event: WebComponentEvent): void {
    if (!this.dependencies.isCloud()) {
      return;
    }

    if (!event.detail.newValue) {
      return;
    }

    // Update account source depending on selected authentication method
    this.accountSource = event.detail.newValue.value;
  }

  private async onADSyncInProgress(event: WebComponentEvent): Promise<void> {
    this.view.setLoadingState();
  }

  private async onADSyncDone(event: WebComponentEvent): Promise<void> {
    this.view.removeLoadingState();
  }

  private setFormValidation() {
    this.formSubscription?.unsubscribe();

    // Set button state depending on form validations
    if (!isHidden(this.view.dom.$targetsSelection)) {
      this.formSubscription = combineLatest([
        this.view.dom.$details.valid$,
        this.view.dom.$loginSecurity.valid$,
        this.view.dom.$roleAndPrivileges.valid$,
        this.view.dom.$targetsSelection.valid$,
      ]).subscribe({
        next: ([detailsValid, loginSecurityValid, roleAndPrivilegesValid, targetsValid]) => {
          this.isFormValid = detailsValid && loginSecurityValid && roleAndPrivilegesValid && targetsValid;
          this.view.dom.$createButton.disabled = !this.isFormValid;
        },
      });
      return;
    }

    this.formSubscription = combineLatest([
      this.view.dom.$details.valid$,
      this.view.dom.$loginSecurity.valid$,
      this.view.dom.$roleAndPrivileges.valid$,
    ]).subscribe({
      next: ([detailsValid, loginSecurityValid, roleAndPrivilegesValid]) => {
        this.isFormValid = detailsValid && loginSecurityValid && roleAndPrivilegesValid;
        console.log('Form Validity Status:', this.isFormValid);
        this.view.dom.$createButton.disabled = !this.isFormValid;
      },
    });
  }

  public async getDataForView(): Promise<LoggedInUserData | null> {
    let viewData: LoggedInUserData | null = null;

    // get current logged in user data
    if (!this.dependencies.isCloud() || !this.dependencies.permissions.canHaveChildCompanies) {
      const dataProvider = new LoggedInUserDataProvider();
      viewData = await dataProvider.getLoggedInUserData();
    }

    return viewData;
  }
}
